export type ContractSectionHeadingStyle =
	| 'bold'
	| 'markdown'
	| 'numbered'
	| 'plain'
	| 'virtual';

export interface ContractSection {
	id: string;
	content: string;
	headingNewline: string;
	headingStyle: ContractSectionHeadingStyle;
	leadingWhitespace: string;
	originalTitle: string;
	rawHeading: string;
	title: string;
	titlePrefix: string;
	titleSuffix: string;
	trailingWhitespace: string;
}

export interface ContractSectionsDocument {
	prefix: string;
	sections: ContractSection[];
}

interface ContractHeading {
	end: number;
	newline: string;
	raw: string;
	start: number;
	style: ContractSectionHeadingStyle;
	title: string;
	titlePrefix: string;
	titleSuffix: string;
}

const getEditableContent = (
	value: string,
): Pick<
	ContractSection,
	'content' | 'leadingWhitespace' | 'trailingWhitespace'
> => {
	const leadingWhitespace = value.match(/^(?:\r\n|\n|\r)+/u)?.[0] ?? '';
	const withoutLeading = value.slice(leadingWhitespace.length);
	const trailingWhitespace =
		withoutLeading.match(/(?:\r\n|\n|\r)+$/u)?.[0] ?? '';

	return {
		content: withoutLeading.slice(
			0,
			withoutLeading.length - trailingWhitespace.length,
		),
		leadingWhitespace,
		trailingWhitespace,
	};
};

const parseHeading = (
	line: string,
): Pick<
	ContractHeading,
	'style' | 'title' | 'titlePrefix' | 'titleSuffix'
> | null => {
	const markdown = line.match(/^(\s*#{1,6}\s+)(.+?)(\s*#*\s*)$/u);

	if (markdown) {
		return {
			style: 'markdown',
			title: markdown[2],
			titlePrefix: markdown[1],
			titleSuffix: markdown[3],
		};
	}

	const bold = line.match(/^(\s*(?:\*\*|__))(.+?)((?:\*\*|__)\s*)$/u);

	if (bold) {
		return {
			style: 'bold',
			title: bold[2],
			titlePrefix: bold[1],
			titleSuffix: bold[3],
		};
	}

	const numbered = line.match(
		/^(\s*\d+(?:\.\d+)*(?:[.)]|\s*[-–—])\s+)(.{3,120}?)(\s*)$/u,
	);

	if (numbered) {
		return {
			style: 'numbered',
			title: numbered[2],
			titlePrefix: numbered[1],
			titleSuffix: numbered[3],
		};
	}

	const plain = line.match(/^(\s*)([A-ZÀ-Ý][^|\n]{2,100}?)(:\s*)$/u);

	if (plain) {
		return {
			style: 'plain',
			title: plain[2],
			titlePrefix: plain[1],
			titleSuffix: plain[3],
		};
	}

	return null;
};

const findHeadings = (value: string): ContractHeading[] => {
	const headings: ContractHeading[] = [];
	const lines = value.match(/[^\r\n]*(?:\r\n|\n|\r|$)/gu) ?? [];
	let isInsideCodeFence = false;
	let offset = 0;

	for (const lineWithNewline of lines) {
		if (lineWithNewline) {
			const newline = lineWithNewline.match(/(?:\r\n|\n|\r)$/u)?.[0] ?? '';
			const line = lineWithNewline.slice(
				0,
				lineWithNewline.length - newline.length,
			);
			const isFence = /^\s*(?:```|~~~)/u.test(line);

			if (!isInsideCodeFence && !isFence) {
				const parsed = parseHeading(line);

				if (parsed) {
					headings.push({
						...parsed,
						end: offset + line.length,
						newline,
						raw: line,
						start: offset,
					});
				}
			}

			if (isFence) {
				isInsideCodeFence = !isInsideCodeFence;
			}

			offset += lineWithNewline.length;
		}
	}

	return headings;
};

export const parseContractSections = (
	value: string,
): ContractSectionsDocument => {
	const headings = findHeadings(value);

	if (headings.length === 0) {
		return {
			prefix: '',
			sections: [
				{
					...getEditableContent(value),
					id: 'contract-section-0',
					headingNewline: '',
					headingStyle: 'virtual',
					originalTitle: 'Conteúdo geral',
					rawHeading: '',
					title: 'Conteúdo geral',
					titlePrefix: '',
					titleSuffix: '',
				},
			],
		};
	}

	return {
		prefix: value.slice(0, headings[0].start),
		sections: headings.map((heading, index) => {
			const bodyStart = heading.end + heading.newline.length;
			const bodyEnd = headings[index + 1]?.start ?? value.length;

			return {
				...getEditableContent(value.slice(bodyStart, bodyEnd)),
				id: `contract-section-${index}`,
				headingNewline: heading.newline,
				headingStyle: heading.style,
				originalTitle: heading.title,
				rawHeading: heading.raw,
				title: heading.title,
				titlePrefix: heading.titlePrefix,
				titleSuffix: heading.titleSuffix,
			};
		}),
	};
};

export const serializeContractSections = (
	document: ContractSectionsDocument,
): string => {
	return (
		document.prefix +
		document.sections
			.map((section) => {
				const heading =
					section.headingStyle === 'virtual'
						? ''
						: section.title === section.originalTitle
							? section.rawHeading
							: `${section.titlePrefix}${section.title}${section.titleSuffix}`;

				return `${heading}${section.headingNewline}${section.leadingWhitespace}${section.content}${section.trailingWhitespace}`;
			})
			.join('')
	);
};
